import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useErrorHandler } from 'react-error-boundary';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import PropTypes from 'prop-types';
import customReserveForm from '../../api/reserve/customReserveForm';
import TopicPath from '../../components/TopicPath';
import SelectionSummary from '../../components/SelectionSummary';
import { saveCustomUserInfo } from '../../redux_toolkit/reserveSlice';
import configMastaApi from '../../api/masta/configMasta';
import {
  INPUT_TYPE_TEXT,
  INPUT_TEXTAREA_FLAG,
  INPUT_DATE_FLAG,
  INPUT_EXP_FLAG,
  INPUT_RADIO_FLAG,
  INPUT_CHECK_FLAG,
  INPUT_SELECT_FLAG,
  INPUT_TELLNUM_FLAG,
  INPUT_ENGMATH_FLAG,
  INPUT_MATHONLY_FLAG,
  INPUT_URL_FLAG,
  INPUT_TYPE_NAME,
  INPUT_TYPE_ADDRESS,
  INPUT_TYPE_BIRTHDAY,
  INPUT_TYPE_PHONE,
  MAX_CHARACTER_LIMIT,
  CANCEL_ANNOUNCE
} from '../../constants';

import CustomParts, {
  TextBox,
  TextArea,
  DateArea,
  RadioArea,
  CheckBoxArea,
  SelectBoxArea,
  TellNumArea,
  MathOnlyArea,
  EngMathArea,
  UrlArea,
} from '../../CustomParts';

import { useNavigateWithId } from '../../customHooks';
import MoveTop from '../../CommonParts';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

function CustomizeReserveForm() {
  const navigate = useNavigateWithId();

  const reserve = useSelector((state) => state.reserve);
  const storeNote = useSelector((state) => state.reserve.note);
  const storeSendType = useSelector((state) => state.reserve.sendType);
  const storeTelNum = useSelector((state) => state.reserve.telNum);
  const storeMailAddress = useSelector((state) => state.reserve.mailAddress);
  const rsvConfig = useSelector((state) => state.rsvConfig);
  const { topicPath = [], maxAttendees = 0 } = rsvConfig;
  const storeNumOfAttendees = useSelector((state) => state.reserve.numOfAttendees);
  const lgCd = useSelector((state) => state.reserve.lgCd);
  const rsvFlag = useSelector((state) => state.reserve.rsvFlag);

  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  // 固定分のstate
  const [note, setNote] = useState(storeNote);
  const [sendType, setSendType] = useState(storeSendType);
  const [smsEnable, setSmsEnable] = useState(false);
  const [mailAddress, setMailAddress] = useState(storeMailAddress);
  const [confMailAddress, setConfMailAddress] = useState('');
  const [telNum, setTelNum] = useState(storeTelNum);
  const [fromMailAddress, setFromMailAddress] = useState('xxx@xxxxxx.ne.jp');
  const [numOfAttendees, setNumOfAttendees] = useState(storeNumOfAttendees);
  const [numOfAttendeesOptions, setNumOfAttendeesOptions] = useState([]);
  const [backUrl, setBackUrl] = useState('');

  // カスタマイズ用
  const storedAnswers = useSelector((state) => state.reserve.answerList);
  const [formSetting, setFormSetting] = useState('');
  const [answerList, setAnswerList] = useState(storedAnswers);
  const [err, setError] = useState([]);
  const [baseName, setBaseName] = useState('');

  const [selectIndex, setSelectIndex] = useState(null);
  const [pageName, setPageName] = useState('');
  const [remarksColumnPlaceholder, setRemarksColumnPlaceholder] = useState('');
  const [remarksColumnLimit, setRemarksColumnLimit] = useState(0);

  // 予約設定マスタの検索
  const getReserveSetting = async () => {
    const { rsvSetId } = reserve;
    const result = await customReserveForm.searchReserveSetting(rsvSetId).catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const { smsEnable, remarksColumnPlaceholder, remarksColumnLimit } = result;
    setSmsEnable(smsEnable);
    setRemarksColumnPlaceholder(remarksColumnPlaceholder);
    setRemarksColumnLimit(remarksColumnLimit);
  };

  // フォーム設定を取得
  const getFormSetting = async () => {
    const { formSetId, rsvSetId } = reserve;
    // デフォルト用にグループIDを引き出す。
    const { data } = await customReserveForm.getGroupId(rsvSetId).catch((err) => {
      window.alert('グループ情報の取得に失敗しました。');
      handleError(err);
    });
    const setting = await customReserveForm.getFormSetting(lgCd, data, formSetId).catch((err) => {
      window.alert('予約フォーム設定の取得に失敗しました。');
      handleError(err);
    });
    const { alternative_name: alternativeName = '', input_info: inputInfo = [] } = setting;
    const startValue = [];
    // もどってきたときは書き込んだデータを使う
    if (answerList.length === 0) {
      // すべての初期値を入れる
      for (let i = 0; i < inputInfo.length; i += 1) {
        const { title, input_type: inputType = 1, require = true } = inputInfo[i];
        if (
          inputType === INPUT_RADIO_FLAG ||
          inputType === INPUT_CHECK_FLAG ||
          inputType === INPUT_SELECT_FLAG
        ) {
          startValue.push({
            question: title,
            inputType,
            answer: [],
            require,
          });
        } else {
          startValue.push({ question: title, inputType, answer: [''], require });
        }
      }
      setAnswerList(startValue);
    }
    setBaseName(alternativeName);
    setFormSetting(setting);
  };

  // 日付のチェック
  const validateDate = (date, question) => {
    const dateArray = date.split('-');
    if (dateArray.length !== 0) {
      if (!dateArray[0] || !dateArray[1] || !dateArray[2]) {
        err.push(`項目:「${question}」は年・月・日を全て入力してください。\n`);
      } else if (new Date(date).toString() === 'Invalid Date') {
        err.push(`項目:「${question}」を正しく入力してください。\n`);
      } else if (
        dayjs(date).isBefore(dayjs('1900-01-01')) ||
        dayjs(date).isAfter(dayjs('2100-12-31'))
      ) {
        err.push(
          `項目:「${question}」は1900年1月1日から2100年12月31日の範囲で入力してください。\n`
        );
      }
    }

    return err;
  };

  /**
   * value内の値の変更を行う。
   */
  const changeSetter = (obj, num) => {
    const newAnswerList = [...answerList];
    newAnswerList[num] = obj;
    setAnswerList(newAnswerList);
  };

  // 氏名のバリデーション
  const validateName = (
    inputMessage,
    characterLimitMessage,
    answer,
    characterLimit,
    question,
    require
  ) => {
    // 必須
    if (require === true && answer[0].trim() === '') {
      err.push(inputMessage);
    }
    if (answer[0].length > characterLimit && characterLimit !== 0) {
      err.push(characterLimitMessage);
    }
    if (
      answer[0].length !== 0 &&
      !/^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}a-zA-Z々ー・＝－ 　]+$/u.test(
        answer[0]
      )
    ) {
      err.push(
        `項目:「${question}」は、ひらがな、全角カタカナ、漢字、半角英語、・＝ーのみで入力してください。\n`
      );
    }
  };

  //  氏名以外のテキスト（制限なしの枠のみ）のバリデーション
  const validateOfOtherName = (
    inputMessage,
    characterLimitMessage,
    answer,
    characterLimit,
    require
  ) => {
    if (require === true && answer[0].trim() === '') {
      err.push(inputMessage);
    }
    if (answer[0].length > characterLimit && characterLimit !== 0) {
      err.push(characterLimitMessage);
    }
  };

  // 選択が一つの項目
  const validateOfOneSelectCheck = (answer, selectMessage, question, require) => {
    if (answer.length === 0 && require === true) {
      err.push(selectMessage);
    }
    if (answer[0] === '') {
      err.push(`項目:「${question}」が正しく選択されていません。\n`);
    }
    if (answer.length > 1) {
      err.push(`項目:「${question}」の選択は1つまでです。\n`);
    }
  };

  // チェックボックスのバリデーション
  const validateOfCheckBox = (answer, selectMessage, question, require) => {
    if (require === true && answer.length === 0) {
      err.push(selectMessage);
    } else if (answer.includes('') === true) {
      err.push(`項目:「${question}」が正しく選択されていません。\n`);
    }
  };

  // 電話番号のバリデーション
  const validateOfTelNum = (answer, inputMessage, question, require) => {
    if (require === true && answer[0].trim() === '') {
      err.push(inputMessage);
    }
    if (answer[0] !== '') {
      const telRegex = /^[0-9]{1,5}-?[0-9]{1,5}-?[0-9]{1,5}$/;
      if (answer[0].length > 13) {
        err.push(`項目:「${question}」は13文字以内で入力してください。\n`);
      }
      if (!telRegex.test(answer[0])) {
        err.push(`項目:「${question}」の形式が正しくありません。\n`);
      }
    }
  };

  // 数字のみのバリデーション
  const validateOfMath = (answer, inputMessage, question, require, characterLimit) => {
    if (require === true && answer[0].trim() === '') {
      err.push(inputMessage);
    }
    if (answer[0].length > characterLimit && characterLimit !== 0) {
      err.push(`項目:「${question}」は${characterLimit}桁以内で入力してください。\n`);
    }
    if (!/^[0-9]+$/.test(answer[0]) && answer[0] !== '') {
      err.push(`項目:「${question}」は半角数字で入力してください。\n`);
    }
    if (answer[0].length > MAX_CHARACTER_LIMIT) {
      err.push(`項目:「${question}」の入力できる最大文字数を超えています。\n`);
    }
  };

  // 英数字の時のバリデーション
  const validateOfEngMath = (
    answer,
    inputMessage,
    question,
    require,
    characterLimit,
    characterLimitMessage
  ) => {
    if (require === true && answer[0].trim() === '') {
      err.push(inputMessage);
    }
    if (answer[0].length > characterLimit && characterLimit !== 0) {
      err.push(characterLimitMessage);
    }
    if (answer[0].length > MAX_CHARACTER_LIMIT) {
      err.push(`項目:「${question}」の入力できる最大文字数を超えています。\n`);
    }
    if (!/^[a-zA-Z0-9]+$/.test(answer[0]) && answer[0] !== '') {
      err.push(`項目:「${question}」は半角英数字で入力してください。\n`);
    }
  };

  // 保存前のバリデーションチェック
  const customValidate = (answerItem, characterLimit, display) => {
    const { question = '', answer = [], require = true, inputType = 1 } = answerItem;

    const inputMessage = `項目:「${question}」を入力してください。\n`;
    const selectMessage = `項目:「${question}」を選択してください。\n`;
    const characterLimitMessage = `項目:「${question}」は${characterLimit}文字以内で入力してください。\n`;

    // 氏名のチェック
    if (inputType === INPUT_TYPE_NAME && display === true) {
      validateName(inputMessage, characterLimitMessage, answer, characterLimit, question, require);
    }
    // 住所
    if (inputType === INPUT_TYPE_ADDRESS && display === true) {
      validateOfOtherName(inputMessage, characterLimitMessage, answer, characterLimit, require);
    }
    // テキスト
    if (inputType === INPUT_TYPE_TEXT) {
      validateOfOtherName(inputMessage, characterLimitMessage, answer, characterLimit, require);
    }
    //  テキストエリアが空
    if (inputType === INPUT_TEXTAREA_FLAG) {
      validateOfOtherName(inputMessage, characterLimitMessage, answer, characterLimit, require);
    }
    // 日付と生年月日
    if ((inputType === INPUT_TYPE_BIRTHDAY && display === true) || inputType === INPUT_DATE_FLAG) {
      if (require === true || answer[0].length !== 0) {
        validateDate(answer[0], question);
      }
    }
    // ラジオボタンのチェック
    if (inputType === INPUT_RADIO_FLAG) {
      validateOfOneSelectCheck(answer, selectMessage, question, require);
    }
    // チェックボックスのチェック
    if (inputType === INPUT_CHECK_FLAG) {
      validateOfCheckBox(answer, selectMessage, question, require);
    }
    // セレクトボックスのチェック
    if (inputType === INPUT_SELECT_FLAG) {
      validateOfOneSelectCheck(answer, selectMessage, question, require);
    }
    // 電話番号のチェック
    if (inputType === INPUT_TELLNUM_FLAG || (inputType === INPUT_TYPE_PHONE && display === true)) {
      validateOfTelNum(answer, inputMessage, question, require);
    }
    // 数字のみ
    if (inputType === INPUT_MATHONLY_FLAG) {
      validateOfMath(answer, inputMessage, question, require, characterLimit);
    }
    // 英数字
    if (inputType === INPUT_ENGMATH_FLAG) {
      validateOfEngMath(
        answer,
        inputMessage,
        question,
        require,
        characterLimit,
        characterLimitMessage
      );
    }

    setError(...err);
  };

  // カスタム項目以外のバリデーション
  const validate = () => {
    // SMS利用時の電話番号チェック
    const regax = new RegExp(/^0[789]0-?[0-9]{4}-?[0-9]{4}$/);
    if ((sendType === 2 || sendType === 3) && !regax.test(telNum)) {
      err.push('SMSを利用する場合は携帯電話の番号を入力してください。\n');
    }

    // メールアドレスが区切り文字でつながっていないかを確認
    const separatorPattern = /[,:;、：；\s\u3000]/;
    if (separatorPattern.test(mailAddress)) {
      err.push('複数のメールアドレスは入力できません。区切り文字（, や ;）を削除し、1件のみ入力してください。\n');
    }

    // メールアドレスのチェック
    if (!mailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      err.push('メールアドレスを入力してください。\n');
    } else if (mailAddress.length > 256) {
      err.push('メールアドレスは256文字以内で入力してください。\n');
    } else {
      // @ または . がないと形式エラー
      const test = new RegExp(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/);
      if (
        (!mailAddress.includes('@') || !mailAddress.includes('.') || !test.test(mailAddress)) &&
        (sendType === 1 || sendType === 3)
      ) {
        err.push('メールアドレスの形式が正しくありません。\n');
      }
    }

    if (!confMailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      err.push('メールアドレス(確認)を入力してください。\n');
    }

    if (
      mailAddress.match(/\S/g) &&
      confMailAddress.match(/\S/g) &&
      mailAddress !== confMailAddress
    ) {
      err.push('メールアドレスが確認用メールアドレスと異なります。\n');
    }

    if (note.length > remarksColumnLimit) {
      err.push(`備考は${remarksColumnLimit}文字以内で入力してください。\n`);
    }

    setError(...err);
  };

  // 「前に戻る」ボタンの押下時
  const onClickBack = () => {
    // reduxに保存
    dispatch(
      saveCustomUserInfo({
        mailAddress,
        numOfAttendees,
        note,
        sendType,
        telNum,
        answerList,
        baseName,
      })
    );
    navigate(`/${backUrl}`);
  };

  // 次への処理
  const onClickNext = async () => {
    const { input_info: inputInfo = [] } = formSetting;
    for (let i = 0; i < inputInfo.length; i += 1) {
      const { character_limit: characterLimit = MAX_CHARACTER_LIMIT, display = true } =
        inputInfo[i];
      customValidate(answerList[i], characterLimit, display);
    }

    validate();
    if (err.length !== 0) {
      // メッセージがあれば、アラートを表示
      const errMessages = err.join(' ');
      window.alert(errMessages);
      setError([]);
      return;
    }

    dispatch(
      saveCustomUserInfo({
        mailAddress,
        numOfAttendees,
        note,
        sendType,
        telNum,
        answerList,
        baseName,
      })
    );
    navigate('/confirmation');
  };

  // パンくずリスト設定から、ページ名・前の画面のURLをstateに設定
  const getBackUrlAndPageName = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    let backUrl = '';
    let scnName = '';
    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        // 「前に戻る」先のURL取得
        const { url: tmpBackUrl = '' } = topicPathConf[i - 1];
        if (tmpBackUrl === '/:lgCd') {
          backUrl = lgCd;
        } else {
          backUrl = tmpBackUrl;
        }

        // pageNameを取得
        scnName = tmpScnName;

        break;
      }
    }

    setPageName(scnName);
    setBackUrl(backUrl);
  };

  // その他参加者の情報
  const createOptions = () => {
    const options = [];
    for (let i = 0; i <= maxAttendees; i += 1) {
      let label = '';
      if (i === 0) {
        label = '無し';
      } else {
        label = `${i}人`;
      }
      options.push(
        <option key={label} value={i}>
          {label}
        </option>
      );
    }
    setNumOfAttendeesOptions(options);
  };

  // 画面が遷移してきたときに走る
  const init = async () => {
    // ページ名/戻る先URLの取得
    getBackUrlAndPageName(topicPath);

    // 送信元メールアドレス設定の取得
    const fromMailConf = await configMastaApi.findByConfCd('rsv_from_mailaddress').catch((err) => {
      window.alert('設定の取得に失敗しました。');
      handleError(err);
    });
    const { value: fromMail = '' } = fromMailConf;
    setFromMailAddress(fromMail);
    createOptions();
  };

  // ページタイトルのsetState
  const getPageTitle = (data) => {
    const { form_title: formTitle = '' } = data;
    return formTitle;
  };

  const getTitleExp = (data) => {
    const { form_explanation: formExplanation = '' } = data;

    return formExplanation;
  };

  /**
   * 入力フォームを作成する。
   * @returns formTable
   */
  const createForm = (data) => {
    const { input_info: inputInfo = [] } = data;
    // 取得したデータから必要な情報を抜き取る。

    const formTable = [];
    for (let i = 0; i < inputInfo.length; i += 1) {
      const {
        input_type: inputType = 1,
        title,
        explanation = '',
        require = true,
        placeholder = '',
        selection = [],
        display = true,
        url = '',
        urlText = '',
      } = inputInfo[i];
      const { answer = [] } = answerList[i];

      // 条件に合った投稿フォームを表示するように配列を構成する。
      //  テキストの時・氏名・住所の時
      if (
        inputType === INPUT_TYPE_TEXT ||
        inputType === INPUT_TYPE_NAME ||
        inputType === INPUT_TYPE_ADDRESS
      ) {
        if (display) {
          formTable.push(
            <MakeText
              question={title}
              inputType={inputType}
              explanation={explanation}
              need={require}
              placeholder={placeholder}
              answer={answer}
              setter={changeSetter}
              num={i}
              key={`text_${i}`}
            />
          );
        }
      }
      // テキストエリア時
      if (inputType === INPUT_TEXTAREA_FLAG) {
        formTable.push(
          <MakeTextArea
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`textArea_${i}`}
          />
        );
      }
      // 日付入力・生年月日の時
      if (inputType === INPUT_DATE_FLAG || inputType === INPUT_TYPE_BIRTHDAY) {
        if (display) {
          formTable.push(
            <MakeDate
              question={title}
              inputType={inputType}
              explanation={explanation}
              need={require}
              answer={answer}
              setter={changeSetter}
              num={i}
              key={`date_${i}`}
            />
          );
        }
      }
      // 説明（入力なし）
      if (inputType === INPUT_EXP_FLAG) {
        const { title = '' } = inputInfo[i];
        if (!answerList[i]) {
          changeSetter(
            { question: title, inputType: 4, answer: ['説明のため回答はありません。'] },
            i
          );
        }
        formTable.push(
          <div className="mb20 txtCenter" key={`exp_${i}`}>
            <span>{explanation}</span>
          </div>
        );
      }
      //   選択肢がradio
      if (inputType === INPUT_RADIO_FLAG) {
        formTable.push(
          <MakeRadioButton
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`radioButton_${i}`}
          />
        );
      }
      // チェックボックス
      if (inputType === INPUT_CHECK_FLAG) {
        formTable.push(
          <MakeCheckBox
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`checkBox_${i}`}
          />
        );
      }
      // セレクトボックス
      if (inputType === INPUT_SELECT_FLAG) {
        formTable.push(
          <MakeSelectBox
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`selectBox_${i}`}
          />
        );
      }
      // 電話番号
      if (inputType === INPUT_TELLNUM_FLAG || inputType === INPUT_TYPE_PHONE) {
        if (display) {
          formTable.push(
            <React.Fragment key={`tellNum_${i}`}>
              <TelNumBox
                question={title}
                inputType={inputType}
                explanation={explanation}
                need={require}
                placeholder={placeholder}
                answer={answer}
                setter={changeSetter}
                num={i}
              />
              {smsEnable === true ? (
                <a
                  role="button"
                  className="tellNumPosting"
                  onClick={() => {
                    setSelectIndex(i);
                    setTelNum(answer[0]);
                  }}
                  tabIndex={0}
                >
                  電話番号を転記
                </a>
              ) : null}
            </React.Fragment>
          );
        }
      }
      // 数字のみ
      if (inputType === INPUT_MATHONLY_FLAG) {
        formTable.push(
          <MakeInt
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`mathOnly_${i}`}
          />
        );
      }
      // 英数字
      if (inputType === INPUT_ENGMATH_FLAG) {
        formTable.push(
          <MakeIntEng
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`engAndMath_${i}`}
          />
        );
      }
      // 外部URL
      if (inputType === INPUT_URL_FLAG) {
        if (!answerList[i]) {
          changeSetter(
            { question: title, inputType: 11, answer: ['外部URLのため、回答はありません。'] },
            i
          );
        }
        formTable.push(
          <MakeUrlItem question={title} explanation={explanation} url={url} urlText={urlText} />
        );
      }
    }

    const newFormTable = formTable.flatMap((item, index) =>
      index === formTable.length - 1 ? [item] : [item, <br key={`br-${index}`} />]
    );

    return newFormTable;
  };

  // 最初に描画する
  useEffect(() => {
    init();
    getReserveSetting();
    getFormSetting();
  }, []);

  return (
    <>
      <TopicPath />
      <SelectionSummary />
      <main>
        <article>
          <div id="contents" className="customForm">
            <MoveTop lgCd={lgCd} />
            <div className="inner">
              <h2>{pageName}</h2>
              <div className="txtCenter" style={{ fontWeight: 'bold' }}>
                {formSetting === '' ? null : getPageTitle(formSetting)}
              </div>
              <p className="formExpStyle">{formSetting === '' ? null : getTitleExp(formSetting)}</p>
              {formSetting === '' ? null : createForm(formSetting)}
            </div>
            <div className="inner">
              {maxAttendees > 0 && rsvFlag === 0 ? (
                <dl className="custom">
                  <dt>同席者数</dt>
                  <span className=" mb20">
                    予約者ご本人様以外に参加者がいる場合は人数を選択してください。
                  </span>
                  <dd>
                    <select
                      name="sankaninzuu"
                      value={numOfAttendees}
                      onChange={(e) => setNumOfAttendees(e.target.value)}
                    >
                      {numOfAttendeesOptions}
                    </select>
                  </dd>
                </dl>
              ) : null}
            </div>
            <div className="inner">
              <dl className="custom">
                <dt>備考</dt>
                <dd>
                  <textarea
                    placeholder={remarksColumnPlaceholder}
                    defaultValue={note}
                    onBlur={(e) => {
                      setNote(e.target.value);
                    }}
                  />
                </dd>
              </dl>
            </div>
            <div className="inner">
              {smsEnable === true ? (
                <dl className="custom">
                  <dt className="mb10">予約完了のご案内</dt>
                  <span>
                    WEB窓口を利用する予定の環境（PC、スマホなど）で受信いただくことを推奨します。
                  </span>
                  <dd>
                    <div className="sendtypeContainer">
                      <label htmlFor="mail" className="sendTypeLabel">
                        <input
                          id="mail"
                          type="radio"
                          name="sendType"
                          value={1}
                          onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                          checked={sendType === 1}
                        />
                        メール
                      </label>
                      <label htmlFor="sms" className="sendTypeLabel">
                        <input
                          id="sms"
                          type="radio"
                          name="sendType"
                          value={2}
                          onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                          checked={sendType === 2}
                        />
                        SMS
                      </label>
                      <label htmlFor="mail&sms" className="sendTypeLabel">
                        <input
                          id="mail&sms"
                          type="radio"
                          name="sendType"
                          value={3}
                          onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                          checked={sendType === 3}
                        />
                        メール+SMS
                      </label>
                    </div>
                  </dd>
                </dl>
              ) : null}
              <div className="mb20">
                {sendType === 1 ? (
                  <span>
                    予約完了時にメールを送信します。
                    <br />
                    {fromMailAddress}からの受信が可能なメールを入力してください。
                  </span>
                ) : null}
                {sendType === 2 ? (
                  <span>
                    予約完了時にSMSを送信します。
                    <br />
                    SMSを利用する場合は電話番号に携帯電話の番号を入力してください。
                  </span>
                ) : null}
                {sendType === 3 ? (
                  <span>
                    予約完了時にメールとSMSを送信します。
                    <br />
                    SMSを利用する場合は電話番号に携帯電話の番号を入力してください。
                    <br />
                    {fromMailAddress}からの受信が可能なメールを入力してください。
                  </span>
                ) : null}
              </div>
              {sendType === 1 ? (
                <dl className="custom">
                  <dt>
                    メールアドレス&nbsp;<span className="required">*必須</span>
                  </dt>
                  <dd>
                    {reserve.useHome ? (
                      <span>
                        WEB窓口利用の際、通信切断などで予約者様との接続ができない時に連絡する場合があります。
                      </span>
                    ) : null}
                    <input
                      type="email"
                      name="email"
                      defaultValue={mailAddress}
                      onChange={(e) => setMailAddress(e.target.value)}
                    />
                  </dd>
                  <dt>
                    メールアドレス&nbsp;<span>（確認用）</span>
                  </dt>
                  <dd>
                    <input
                      type="email"
                      name="email2"
                      defaultValue={confMailAddress}
                      onChange={(e) => setConfMailAddress(e.target.value)}
                      onPaste={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      autoComplete="off"
                    />
                  </dd>
                </dl>
              ) : null}
              {sendType === 2 ? (
                <dl className="custom">
                  <dt>
                    電話番号（SMS送信用）&nbsp;<span className="required">*必須</span>
                  </dt>
                  <dd>
                    {reserve.useHome ? (
                      <span>
                        WEB窓口利用の際、通信切断などで予約者様との接続ができない時に連絡する場合があります。
                      </span>
                    ) : null}
                    <input
                      type="tel"
                      name="telNum"
                      value={telNum}
                      onChange={(e) => setTelNum(e.target.value)}
                    />
                  </dd>
                </dl>
              ) : null}
              {sendType === 3 ? (
                <dl className="custom">
                  <dt>
                    メール&nbsp;<span className="required">*必須</span>
                  </dt>
                  <dd>
                    <input
                      type="email"
                      name="email"
                      defaultValue={mailAddress}
                      onChange={(e) => setMailAddress(e.target.value)}
                    />
                  </dd>
                  <dt>
                    メール&nbsp;<span>（確認用）</span>
                  </dt>
                  <dd>
                    <input
                      type="email"
                      name="email2"
                      defaultValue={confMailAddress}
                      onChange={(e) => setConfMailAddress(e.target.value)}
                      onPaste={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      autoComplete="off"
                    />
                    <br />
                    <span>
                      WEB窓口利用の際、通信切断などで予約者様との接続ができない時に連絡する場合があります。
                    </span>
                  </dd>
                  <dt>
                    電話番号（SMS送信用）&nbsp;<span className="required">*必須</span>
                  </dt>
                  <dd>
                    <input
                      type="tel"
                      name="telNum"
                      value={telNum}
                      onChange={(e) => setTelNum(e.target.value)}
                    />
                  </dd>
                </dl>
              ) : null}
            </div>

            <div className="inner">
              <ul id="navi" className="row jcSB">
                <li className="col2">
                  <a role="button" onClick={onClickBack} className="btn btBack" tabIndex={0}>
                    前に戻る
                  </a>
                </li>
                <li className="col2 mr0 row jcFE">
                  <a role="button" className="btn btNext" onClick={onClickNext} tabIndex={0}>
                    入力内容を確定し次へ
                  </a>
                </li>
              </ul>
            </div>
            <div className='cancelAnnounce'>{CANCEL_ANNOUNCE}</div>
          </div>
        </article>
      </main>
    </>
  );
}

/**
 * テキスト欄を構成する。
 * @param {object} 親要素からの引数
 * @returns テキスト欄の構成要素
 */
export function MakeText({
  question,
  inputType,
  explanation,
  need,
  placeholder,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <TextBox
          question={question}
          need={need}
          answer={answer}
          placeholder={placeholder}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeText.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * テキストエリアを構成する。
 * @param {object} 親要素からの引数
 * @returns テキストエリアの表示要素
 */
export function MakeTextArea({
  question,
  inputType,
  explanation,
  need,
  placeholder,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <TextArea
          question={question}
          need={need}
          answer={answer}
          placeholder={placeholder}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeTextArea.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * 日付入力
 * @param {object} 親要素からの引数
 * @returns 日付入力の表示要素
 */
export function MakeDate({ question, inputType, explanation, need, answer, setter, num }) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <DateArea
          question={question}
          need={need}
          answer={answer}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeDate.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * ラジオボタン
 * @param {object} 親要素からの引数
 * @returns ラジオボタン表示要素
 */
export function MakeRadioButton({
  question,
  inputType,
  explanation,
  need,
  selection,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <RadioArea
          question={question}
          need={need}
          selection={selection}
          inputType={inputType}
          num={num}
          setter={setter}
          answer={answer}
        />
      }
    />
  );
}
MakeRadioButton.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * チェックボックスの作成
 * @param {object} 親要素からの引数
 * @returns チェックボックスの表示要素
 */
export function MakeCheckBox({
  question,
  inputType,
  explanation,
  need,
  selection,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <CheckBoxArea
          question={question}
          need={need}
          selection={selection}
          answer={answer}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeCheckBox.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * セレクトボックスの作成
 * @param {object} 親要素からの引数
 * @returns セレクトボックスの表示要素
 */
export function MakeSelectBox({
  question,
  inputType,
  explanation,
  need,
  selection,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <SelectBoxArea
          question={question}
          need={need}
          selection={selection}
          answer={answer}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeSelectBox.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * 電話番号欄の作成
 * @param {object} 親要素からの引数
 * @returns 電話番号の構成要素
 */
export function TelNumBox({ question, inputType, explanation, need, placeholder, answer, setter, num }) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <TellNumArea
          question={question}
          need={need}
          answer={answer}
          placeholder={placeholder}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
TelNumBox.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * 数字のみの欄を構成する。
 * @param {object} 親要素からの引数
 * @returns 数字のみの欄の構成要素
 */
export function MakeInt({
  question,
  inputType,
  explanation,
  need,
  placeholder,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <MathOnlyArea
          question={question}
          need={need}
          answer={answer}
          placeholder={placeholder}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeInt.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * 英数字欄を構成する。
 * @param {object} 親要素からの引数
 * @returns 英数字欄の構成要素
 */
export function MakeIntEng({
  question,
  inputType,
  explanation,
  need,
  placeholder,
  answer,
  setter,
  num,
}) {
  return (
    <CustomParts
      question={question}
      need={need}
      explanation={explanation}
      html={
        <EngMathArea
          question={question}
          need={need}
          answer={answer}
          placeholder={placeholder}
          inputType={inputType}
          num={num}
          setter={setter}
        />
      }
    />
  );
}
MakeIntEng.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  need: PropTypes.bool,
  placeholder: PropTypes.string,
  selection: PropTypes.array,
  answer: PropTypes.array,
  setter: PropTypes.func,
  num: PropTypes.number,
}.isRequired;

/**
 * 外部URL欄を構成する。
 * @param {object} 親要素からの引数
 * @returns 外部URL欄の構成要素
 */
export function MakeUrlItem({ question, explanation, url, urlText }) {
  return (
    <CustomParts
      question={question}
      explanation={explanation}
      html={<UrlArea url={url} urlText={urlText} />}
    />
  );
}
MakeUrlItem.propTypes = {
  question: PropTypes.string,
  inputType: PropTypes.number,
  explanation: PropTypes.string,
  num: PropTypes.number,
}.isRequired;

export default CustomizeReserveForm;
